<template>
    <div class="dashboard-container">
        <!-- 顶部搜索栏 -->
        <div class="filter-container">
            <div class="filter-left">
                <span class="page-title">请求日志分析</span>
            </div>
            <div class="filter-right">
                <Form
                    :model="searchForm"
                    inline
                    :label-width="0"
                    class="compact-form"
                >
                    <FormItem>
                        <Select
                            v-model="searchForm.type"
                            style="width: 100px"
                            @on-change="handleTypeChange"
                            placeholder="维度"
                        >
                            <Option value="hourly">按小时</Option>
                            <Option value="daily">按天</Option>
                            <Option value="monthly">按月</Option>
                        </Select>
                    </FormItem>
                    <FormItem>
                        <DatePicker
                            v-model="searchForm.startDate"
                            type="date"
                            :format="dateFormat"
                            placeholder="开始日期"
                            style="width: 140px"
                            :clearable="false"
                        ></DatePicker>
                        <span class="date-split">-</span>
                        <DatePicker
                            v-model="searchForm.endDate"
                            type="date"
                            :format="dateFormat"
                            placeholder="结束日期"
                            style="width: 140px"
                            :clearable="false"
                        ></DatePicker>
                    </FormItem>
                    <FormItem>
                        <Button
                            type="primary"
                            icon="ios-search"
                            @click="handleSearch"
                            :loading="loading"
                            class="action-btn"
                            >查询</Button
                        >
                        <Button
                            icon="md-download"
                            @click="handleExport"
                            class="action-btn export-btn"
                            >导出</Button
                        >
                    </FormItem>
                </Form>
            </div>
        </div>

        <!-- 核心指标卡片 -->
        <Row :gutter="20" class="stat-row">
            <Col
                :xs="24"
                :sm="12"
                :md="6"
                v-for="(item, index) in statCards"
                :key="index"
            >
                <div class="stat-card" :class="item.class">
                    <div class="stat-icon-wrapper">
                        <Icon :type="item.icon" />
                    </div>
                    <div class="stat-info">
                        <div class="stat-label">{{ item.label }}</div>
                        <div class="stat-value">
                            <span v-if="item.prefix" class="unit">{{
                                item.prefix
                            }}</span>
                            {{ item.value }}
                            <span v-if="item.suffix" class="unit">{{
                                item.suffix
                            }}</span>
                        </div>
                    </div>
                    <div class="card-bg-icon">
                        <Icon :type="item.icon" />
                    </div>
                </div>
            </Col>
        </Row>

        <!-- 图表区域 -->
        <div class="chart-section">
            <Row :gutter="20">
                <Col :span="12">
                    <div class="chart-wrapper">
                        <div class="chart-header">
                            <div class="chart-title">请求量趋势</div>
                            <div class="chart-action">
                                <Icon type="ios-pulse" />
                            </div>
                        </div>
                        <div ref="requestCountChart" class="chart-canvas"></div>
                    </div>
                </Col>
                <Col :span="12">
                    <div class="chart-wrapper">
                        <div class="chart-header">
                            <div class="chart-title">响应耗时分析</div>
                            <div class="chart-action">
                                <Icon type="ios-time-outline" />
                            </div>
                        </div>
                        <div ref="responseTimeChart" class="chart-canvas"></div>
                    </div>
                </Col>
            </Row>

            <Row :gutter="20" style="margin-top: 20px">
                <Col :span="12">
                    <div class="chart-wrapper">
                        <div class="chart-header">
                            <div class="chart-title">接口成功率</div>
                            <div class="chart-action">
                                <Icon type="ios-pie-outline" />
                            </div>
                        </div>
                        <div ref="successRateChart" class="chart-canvas"></div>
                    </div>
                </Col>
                <Col :span="12">
                    <div class="chart-wrapper">
                        <div class="chart-header">
                            <div class="chart-title">异常分布</div>
                            <div class="chart-action">
                                <Icon type="ios-alert-outline" />
                            </div>
                        </div>
                        <div ref="errorCountChart" class="chart-canvas"></div>
                    </div>
                </Col>
            </Row>
        </div>

        <!-- 数据表格 -->
        <div class="table-section">
            <div class="section-header">
                <span class="section-title">详细数据报表</span>
            </div>
            <Table
                :columns="tableColumns"
                :data="tableData"
                :loading="loading"
                :row-class-name="rowClassName"
                size="small"
            >
                <!-- 自定义 Slot 渲染成功率 -->
                <template slot-scope="{ row }" slot="successRate">
                    <div class="status-cell">
                        <span
                            class="status-dot"
                            :class="getSuccessRateColor(row.successRate)"
                        ></span>
                        <span>{{
                            row.successRate
                                ? row.successRate.toFixed(2) + "%"
                                : "0.00%"
                        }}</span>
                    </div>
                </template>
            </Table>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import {
    getHourlyStatistics,
    getDailyStatistics,
    getMonthlyStatistics,
} from "@/api/modules/system-management/request-log-management";

export default {
    name: "RequestLogStatistic",
    data() {
        return {
            loading: false,
            searchForm: {
                type: "daily",
                startDate: null,
                endDate: null,
            },
            dateFormat: "yyyy-MM-dd",
            statisticsData: [],
            // 图表实例
            charts: {
                request: null,
                response: null,
                rate: null,
                error: null,
            },
            tableColumns: [],
            tableData: [],
            resizeTimer: null,
        };
    },
    computed: {
        // 构造卡片数据，方便v-for渲染
        statCards() {
            return [
                {
                    label: "总请求数",
                    value: this.formatNumber(this.totalRequests),
                    icon: "ios-podium",
                    class: "card-blue",
                },
                {
                    label: "错误请求",
                    value: this.formatNumber(this.totalErrors),
                    icon: "ios-close-circle",
                    class: "card-red",
                },
                {
                    label: "整体成功率",
                    value: this.avgSuccessRate,
                    suffix: "%",
                    icon: "ios-checkmark-circle",
                    class: "card-green",
                },
                {
                    label: "平均响应",
                    value: this.formatNumber(this.avgDuration),
                    suffix: "ms",
                    icon: "ios-timer",
                    class: "card-orange",
                },
            ];
        },
        totalRequests() {
            if (!this.statisticsData.length) return 0;
            return this.statisticsData.reduce(
                (sum, item) => sum + (item.count || 0),
                0
            );
        },
        totalErrors() {
            if (!this.statisticsData.length) return 0;
            return this.statisticsData.reduce(
                (sum, item) => sum + (item.errorCount || 0),
                0
            );
        },
        avgSuccessRate() {
            if (!this.statisticsData.length) return "0.00";
            const total = this.statisticsData.reduce(
                (sum, item) => sum + (item.successRate || 0),
                0
            );
            return (total / this.statisticsData.length).toFixed(2);
        },
        avgDuration() {
            if (!this.statisticsData.length) return 0;
            const total = this.statisticsData.reduce(
                (sum, item) => sum + (item.avgDuration || 0),
                0
            );
            return Math.round(total / this.statisticsData.length);
        },
    },
    created() {
        this.setDefaultDates();
    },
    mounted() {
        this.$nextTick(() => {
            this.initCharts();
            this.handleSearch();
            window.addEventListener("resize", this.handleResize);
        });
    },
    beforeDestroy() {
        this.destroyCharts();
        window.removeEventListener("resize", this.handleResize);
    },
    methods: {
        setDefaultDates() {
            const now = new Date();
            this.searchForm.endDate = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate()
            );

            if (this.searchForm.type === "daily") {
                this.searchForm.startDate = new Date(
                    now.getTime() - 29 * 24 * 60 * 60 * 1000
                );
            } else if (this.searchForm.type === "monthly") {
                this.searchForm.startDate = new Date(
                    now.getFullYear(),
                    now.getMonth() - 11,
                    1
                );
            } else {
                this.searchForm.startDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate()
                );
            }
        },
        handleTypeChange() {
            this.setDefaultDates();
            // 可选：切换类型时自动查询
            // this.handleSearch();
        },
        handleSearch() {
            if (!this.searchForm.startDate || !this.searchForm.endDate) {
                this.$Message.warning("请选择日期范围");
                return;
            }
            if (
                new Date(this.searchForm.startDate) >
                new Date(this.searchForm.endDate)
            ) {
                this.$Message.warning("开始日期不能大于结束日期");
                return;
            }

            this.loading = true;
            const requestBody = {
                startDate: this.formatDate(this.searchForm.startDate),
                endDate: this.formatDate(this.searchForm.endDate),
            };

            let apiPromise;
            switch (this.searchForm.type) {
                case "hourly":
                    apiPromise = getHourlyStatistics(requestBody);
                    break;
                case "daily":
                    apiPromise = getDailyStatistics(requestBody);
                    break;
                case "monthly":
                    apiPromise = getMonthlyStatistics(requestBody);
                    break;
                default:
                    this.loading = false;
                    return;
            }

            apiPromise
                .then((response) => {
                    if (response && response.code === 0) {
                        this.statisticsData = response.data || [];
                        this.updateCharts();
                        this.updateTable();
                    } else {
                        // 容错处理
                        if (
                            typeof response === "string" &&
                            response.includes("html")
                        ) {
                            this.$Message.error("登录已过期，请刷新页面");
                            return;
                        }
                        this.$Message.error(
                            (response && response.message) || "获取数据失败"
                        );
                        this.statisticsData = [];
                        this.updateCharts();
                        this.updateTable();
                    }
                })
                .catch((error) => {
                    this.$Message.error("网络错误");
                    this.statisticsData = [];
                    this.updateCharts();
                    this.updateTable();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleExport() {
            this.$Message.info("导出功能正在开发中...");
        },
        // 工具函数
        formatDate(date) {
            if (!date) return "";
            const d = new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                2,
                "0"
            )}-${String(d.getDate()).padStart(2, "0")}`;
        },
        formatDateTime(dateTime) {
            if (!dateTime) return "";
            const d = new Date(dateTime);
            const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                2,
                "0"
            )}-${String(d.getDate()).padStart(2, "0")}`;
            return this.searchForm.type === "hourly"
                ? `${ymd} ${String(d.getHours()).padStart(2, "0")}:00`
                : ymd;
        },
        formatNumber(num) {
            return num ? num.toLocaleString() : "0";
        },
        formatSize(bytes) {
            if (!bytes) return "0 B";
            const k = 1024;
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return (
                parseFloat((bytes / Math.pow(k, i)).toFixed(2)) +
                " " +
                ["B", "KB", "MB", "GB"][i]
            );
        },
        getSuccessRateColor(rate) {
            if (!rate) return "dot-red";
            if (rate >= 99) return "dot-green";
            if (rate >= 90) return "dot-orange";
            return "dot-red";
        },

        // 图表逻辑
        initCharts() {
            const chartOptions = { renderer: "canvas" }; // 性能优化
            this.charts.request = echarts.init(
                this.$refs.requestCountChart,
                null,
                chartOptions
            );
            this.charts.response = echarts.init(
                this.$refs.responseTimeChart,
                null,
                chartOptions
            );
            this.charts.rate = echarts.init(
                this.$refs.successRateChart,
                null,
                chartOptions
            );
            this.charts.error = echarts.init(
                this.$refs.errorCountChart,
                null,
                chartOptions
            );
        },
        updateCharts() {
            if (!this.statisticsData.length) {
                Object.values(this.charts).forEach(
                    (chart) => chart && chart.clear()
                );
                return;
            }

            const xData = this.statisticsData.map((item) => {
                if (this.searchForm.type === "monthly")
                    return `${item.year}-${item.month}`;
                return this.searchForm.type === "hourly"
                    ? this.formatDateTime(item.dateTime)
                    : this.formatDateTime(item.date);
            });

            // 通用样式配置
            const commonGrid = {
                left: "4%",
                right: "4%",
                bottom: "10%",
                top: "15%",
                containLabel: true,
            };
            const commonTooltip = {
                trigger: "axis",
                backgroundColor: "rgba(255,255,255,0.95)",
                textStyle: { color: "#333" },
                extraCssText:
                    "box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 4px;",
            };

            // 1. 请求量
            this.charts.request.setOption({
                tooltip: commonTooltip,
                grid: commonGrid,
                xAxis: {
                    type: "category",
                    data: xData,
                    boundaryGap: false,
                    axisLine: { lineStyle: { color: "#eee" } },
                    axisLabel: { color: "#666" },
                },
                yAxis: {
                    type: "value",
                    splitLine: { lineStyle: { type: "dashed", color: "#eee" } },
                },
                series: [
                    {
                        name: "请求量",
                        type: "line",
                        smooth: true,
                        showSymbol: false,
                        itemStyle: { color: "#3b82f6" },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(
                                0,
                                0,
                                0,
                                1,
                                [
                                    {
                                        offset: 0,
                                        color: "rgba(59,130,246,0.3)",
                                    },
                                    {
                                        offset: 1,
                                        color: "rgba(59,130,246,0.01)",
                                    },
                                ]
                            ),
                        },
                        data: this.statisticsData.map((i) => i.count || 0),
                    },
                ],
            });

            // 2. 响应时间
            this.charts.response.setOption({
                tooltip: commonTooltip,
                grid: commonGrid,
                legend: { right: 10, top: 0, icon: "circle" },
                xAxis: {
                    type: "category",
                    data: xData,
                    axisLine: { lineStyle: { color: "#eee" } },
                },
                yAxis: {
                    type: "value",
                    splitLine: { lineStyle: { type: "dashed", color: "#eee" } },
                },
                series: [
                    {
                        name: "平均耗时",
                        type: "bar",
                        barMaxWidth: 20,
                        itemStyle: {
                            color: "#10b981",
                            borderRadius: [4, 4, 0, 0],
                        },
                        data: this.statisticsData.map(
                            (i) => i.avgDuration || 0
                        ),
                    },
                    {
                        name: "最大耗时",
                        type: "line",
                        smooth: true,
                        itemStyle: { color: "#f59e0b" },
                        data: this.statisticsData.map(
                            (i) => i.maxDuration || 0
                        ),
                    },
                ],
            });

            // 3. 成功率
            this.charts.rate.setOption({
                tooltip: { ...commonTooltip, formatter: "{b}<br/>{a}: {c}%" },
                grid: commonGrid,
                xAxis: {
                    type: "category",
                    data: xData,
                    axisLine: { lineStyle: { color: "#eee" } },
                },
                yAxis: {
                    type: "value",
                    min: 80,
                    max: 100,
                    splitLine: { lineStyle: { type: "dashed", color: "#eee" } },
                },
                visualMap: {
                    show: false,
                    pieces: [
                        { lte: 90, color: "#ef4444" },
                        { gt: 90, lte: 98, color: "#f59e0b" },
                        { gt: 98, color: "#10b981" },
                    ],
                },
                series: [
                    {
                        name: "成功率",
                        type: "line",
                        smooth: true,
                        markLine: { data: [{ type: "average", name: "平均" }] },
                        data: this.statisticsData.map(
                            (i) => i.successRate || 0
                        ),
                    },
                ],
            });

            // 4. 错误数
            this.charts.error.setOption({
                tooltip: commonTooltip,
                grid: commonGrid,
                xAxis: {
                    type: "category",
                    data: xData,
                    axisLine: { lineStyle: { color: "#eee" } },
                },
                yAxis: {
                    type: "value",
                    splitLine: { lineStyle: { type: "dashed", color: "#eee" } },
                },
                series: [
                    {
                        name: "错误数",
                        type: "bar",
                        barMaxWidth: 20,
                        itemStyle: {
                            color: "#ef4444",
                            borderRadius: [4, 4, 0, 0],
                        },
                        data: this.statisticsData.map((i) => i.errorCount || 0),
                    },
                ],
            });
        },
        destroyCharts() {
            Object.values(this.charts).forEach((c) => c && c.dispose());
            this.charts = {};
        },
        handleResize() {
            if (this.resizeTimer) clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                Object.values(this.charts).forEach((c) => c && c.resize());
            }, 200);
        },

        // 表格逻辑
        updateTable() {
            const commonCols = [
                {
                    title: "请求数",
                    key: "count",
                    align: "right",
                    render: (h, p) => h("span", this.formatNumber(p.row.count)),
                },
                {
                    title: "平均耗时",
                    key: "avgDuration",
                    align: "right",
                    render: (h, p) => h("span", p.row.avgDuration + " ms"),
                },
                {
                    title: "错误数",
                    key: "errorCount",
                    align: "right",
                    render: (h, p) =>
                        h(
                            "span",
                            {
                                style: {
                                    color:
                                        p.row.errorCount > 0
                                            ? "#ef4444"
                                            : "#ccc",
                                },
                            },
                            this.formatNumber(p.row.errorCount)
                        ),
                },
                {
                    title: "成功率",
                    slot: "successRate",
                    align: "center",
                    width: 140,
                },
            ];

            if (this.searchForm.type === "monthly") {
                this.tableColumns = [
                    {
                        title: "年月",
                        key: "year",
                        align: "center",
                        width: 120,
                        render: (h, p) =>
                            h(
                                "strong",
                                `${p.row.year}-${String(p.row.month).padStart(
                                    2,
                                    "0"
                                )}`
                            ),
                    },
                    ...commonCols,
                    {
                        title: "最大耗时",
                        key: "maxDuration",
                        align: "right",
                        width: 100,
                    },
                    {
                        title: "独立用户",
                        key: "uniqueUserCount",
                        align: "right",
                        width: 100,
                    },
                ];
            } else if (this.searchForm.type === "hourly") {
                this.tableColumns = [
                    {
                        title: "时间点",
                        key: "dateTime",
                        width: 160,
                        render: (h, p) =>
                            h("strong", this.formatDateTime(p.row.dateTime)),
                    },
                    ...commonCols,
                    {
                        title: "最大耗时",
                        key: "maxDuration",
                        align: "right",
                        width: 100,
                    },
                ];
            } else {
                this.tableColumns = [
                    {
                        title: "日期",
                        key: "date",
                        width: 140,
                        render: (h, p) =>
                            h("strong", this.formatDateTime(p.row.date)),
                    },
                    ...commonCols,
                    {
                        title: "总流量(出)",
                        key: "totalResponseSize",
                        align: "right",
                        width: 120,
                        render: (h, p) =>
                            h("span", this.formatSize(p.row.totalResponseSize)),
                    },
                    {
                        title: "独立用户",
                        key: "uniqueUserCount",
                        align: "right",
                        width: 100,
                    },
                ];
            }
            this.tableData = this.statisticsData;
        },
        rowClassName(row, index) {
            return index % 2 === 1 ? "table-stripe-row" : "";
        },
    },
};
</script>

<style scoped lang="less">
/* 变量定义 */
@primary-color: #3b82f6;
@bg-color: #f5f7f9;
@card-bg: #ffffff;
@text-main: #1f2937;
@text-secondary: #6b7280;

.dashboard-container {
    padding: 24px;
    background-color: @bg-color;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        "Helvetica Neue", Arial, sans-serif;
}

/* 1. 顶部搜索栏 */
.filter-container {
    background: @card-bg;
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    .filter-left {
        .page-title {
            font-size: 18px;
            font-weight: 700;
            color: @text-main;
            border-left: 4px solid @primary-color;
            padding-left: 12px;
        }
    }

    .filter-right {
        .compact-form {
            margin-bottom: 0;
            /deep/ .ivu-form-item {
                margin-bottom: 0;
                margin-right: 12px;
                &:last-child {
                    margin-right: 0;
                }
            }
        }
        .date-split {
            margin: 0 8px;
            color: #ccc;
        }
        .action-btn {
            border-radius: 6px;
            height: 32px;
            &.export-btn {
                background-color: #fff;
                color: #666;
                border-color: #dcdee2;
                &:hover {
                    color: @primary-color;
                    border-color: @primary-color;
                }
            }
        }
    }
}

/* 2. 统计卡片 */
.stat-row {
    margin-bottom: 24px;
}
.stat-card {
    background: @card-bg;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
    cursor: default;
    height: 120px;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 15px rgba(0, 0, 0, 0.08);
    }

    .stat-icon-wrapper {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        margin-right: 16px;
        flex-shrink: 0;
    }

    .stat-info {
        z-index: 2;
        .stat-label {
            font-size: 14px;
            color: @text-secondary;
            margin-bottom: 4px;
        }
        .stat-value {
            font-size: 28px;
            font-weight: 700;
            color: @text-main;
            line-height: 1.2;
            .unit {
                font-size: 14px;
                color: #9ca3af;
                font-weight: normal;
                margin-left: 4px;
            }
        }
    }

    /* 背景水印图标 */
    .card-bg-icon {
        position: absolute;
        right: -10px;
        bottom: -10px;
        font-size: 100px;
        opacity: 0.08;
        transform: rotate(-15deg);
        z-index: 1;
        transition: transform 0.3s;
    }
    &:hover .card-bg-icon {
        transform: rotate(0deg) scale(1.1);
    }

    /* 颜色主题 */
    &.card-blue {
        .stat-icon-wrapper {
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
        }
    }
    &.card-red {
        .stat-icon-wrapper {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
        }
    }
    &.card-green {
        .stat-icon-wrapper {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
        }
    }
    &.card-orange {
        .stat-icon-wrapper {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
        }
    }
}

/* 3. 图表区域 */
.chart-section {
    margin-bottom: 24px;
}
.chart-wrapper {
    background: @card-bg;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .chart-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        .chart-title {
            font-size: 16px;
            font-weight: 600;
            color: @text-main;
        }
        .chart-action {
            color: #ccc;
            cursor: pointer;
            &:hover {
                color: @primary-color;
            }
        }
    }
    .chart-canvas {
        width: 100%;
        height: 320px;
    }
}

/* 4. 表格区域 */
.table-section {
    background: @card-bg;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .section-header {
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #f3f4f6;
        .section-title {
            font-size: 16px;
            font-weight: 600;
            color: @text-main;
        }
    }
}

/* 状态圆点样式 */
.status-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
        &.dot-green {
            background-color: #10b981;
            box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
        }
        &.dot-orange {
            background-color: #f59e0b;
            box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
        }
        &.dot-red {
            background-color: #ef4444;
            box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
        }
    }
}

/* 覆盖 IVIEW 默认样式 */
/deep/ .ivu-table-wrapper {
    border: none;
}
/deep/ .ivu-table:before {
    height: 0;
}
/deep/ .ivu-table:after {
    width: 0;
}
/deep/ .ivu-table th {
    background-color: #f9fafb;
    color: @text-secondary;
    font-weight: 600;
    border-bottom: 1px solid #e5e7eb;
}
/deep/ .ivu-table td {
    border-bottom: 1px solid #f3f4f6;
    color: @text-main;
}
/deep/ .ivu-input,
/deep/ .ivu-select-selection,
/deep/ .ivu-btn {
    box-shadow: none;
}

/* 响应式调整 */
@media (max-width: 992px) {
    .filter-container {
        flex-direction: column;
        align-items: flex-start;
    }
    .filter-right {
        margin-top: 12px;
        width: 100%;
    }
    .compact-form {
        display: flex;
        flex-wrap: wrap;
    }
}
</style>
